import { AlertTriangle, CheckCircle2, Info, ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

type Tone = 'info' | 'success' | 'warning' | 'danger';

const TONES: Record<Tone, { box: string; icon: ReactNode }> = {
  info: { box: 'border-tech/30 bg-tech/[0.07] text-fg', icon: <Info className="h-4 w-4 text-tech" /> },
  success: {
    box: 'border-ok/30 bg-ok/[0.07] text-fg',
    icon: <CheckCircle2 className="h-4 w-4 text-ok" />,
  },
  warning: {
    box: 'border-warn/30 bg-warn/[0.07] text-fg',
    icon: <AlertTriangle className="h-4 w-4 text-warn" />,
  },
  danger: {
    box: 'border-danger/30 bg-danger/[0.07] text-fg',
    icon: <ShieldAlert className="h-4 w-4 text-danger" />,
  },
};

export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: Tone;
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  const { box, icon } = TONES[tone];
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cn('flex gap-3 rounded-lg border px-4 py-3 text-sm', box, className)}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={cn(title && 'mt-1', 'text-fg/90')}>{children}</div> : null}
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-card border border-dashed border-border bg-surface/50 px-6 py-12 text-center">
      {icon ? <div className="mx-auto mb-3 text-faint">{icon}</div> : null}
      <p className="font-semibold text-fg">{title}</p>
      {description ? <p className="mx-auto mt-1 max-w-md text-sm text-muted">{description}</p> : null}
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </div>
  );
}
